import { IDropdownStyles, TooltipHost } from '@fluentui/react';
import { t } from 'i18next';
import { Dropdown } from '@fluentui/react';
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';

export interface Option {
  key: string;
  text: string;
  checked: boolean;
  title?: string;
  ariaLabel?: string;
  active?:  "1" | "0";
}

interface CheckboxFilterComponentProps {
  title: string;
  initialOptions: Option[];
  selectedKeys?: string[];
  onApply: (selectedOptions: Option[]) => void;
  titleStyle?: Partial<IDropdownStyles>;
  dropdownStyle?: React.CSSProperties;
  isClean?:boolean;
  hiddenTooltip?: boolean;
}

const CheckboxFilterComponent: React.FC<CheckboxFilterComponentProps> = ({ title, initialOptions, selectedKeys, onApply, titleStyle, dropdownStyle, isClean, hiddenTooltip }) => {
  const [isListVisible, setIsListVisible] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [tempOptions, setTempOptions] = useState<Option[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [valueSelec, setValueSelec] = useState(selectedKeys ? selectedKeys.join(',') : '');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  // Posicion calculada dinamicamente para que el panel no se salga de la pantalla:
  // por defecto se abre pegado a la izquierda del control, pero si no entra a la derecha
  // (por ejemplo cuando el filtro esta en la ultima columna) se abre pegado a la derecha,
  // y si no entra hacia abajo se abre hacia arriba.
  const [panelPosition, setPanelPosition] = useState<React.CSSProperties>({});

  // Manejo de estado interno para opciones seleccionadas si selectedKeys no se pasa.
  const [internalSelectedKeys, setInternalSelectedKeys] = useState<string[]>([]);

  useEffect(() => {
    if (selectedKeys && selectedKeys.length === 0) {
      setValueSelec('');
    }
  }, [selectedKeys]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsListVisible(false);
        dropdownRef.current.hidden = true;
        setTempOptions([...options]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [options]);

  useEffect(() => {
    const effectiveSelectedKeys = selectedKeys || internalSelectedKeys;
    const updatedOptions = initialOptions.map((option) => ({
      ...option,
      checked: effectiveSelectedKeys.includes(option.text),
    }));
    setOptions(updatedOptions);
    setTempOptions(updatedOptions);
    setSelectAll(updatedOptions.every((option) => option.checked));
  }, [initialOptions, selectedKeys, internalSelectedKeys]);

  useEffect(() => {
    if (isClean) {
      handleClearAll();
    }
  }, [isClean]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setTempOptions(options.filter((option) => option.text.toLowerCase().includes(e.target.value.toLowerCase())));
  };

  const filteredOptions = tempOptions.filter((option) => option.text.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleOptionChange = (key: string) => {
    const updatedOptions = [...tempOptions];
    const index = updatedOptions.findIndex((option) => option.key === key);
    if (index !== -1) {
      updatedOptions[index].checked = !updatedOptions[index].checked;
      setTempOptions(updatedOptions);
    }

    const allChecked = updatedOptions.every((option) => option.checked);
    setValueSelec(
      updatedOptions
        .filter((i) => i.checked)
        .map((i) => i.text)
        .join(','),
    );
    setSelectAll(allChecked);
  };

  const handleSelectAllChange = () => {
    const updatedOptions = tempOptions.map((option) => ({
      ...option,
      checked: !selectAll,
    }));
    setTempOptions(updatedOptions);
    setSelectAll(!selectAll);
    setValueSelec(
      updatedOptions
        .filter((i) => i.checked)
        .map((i) => i.text)
        .join(','),
    );
  };

  const handleApply = () => {
    const selectedOptions = tempOptions.filter((option) => option.checked);
    if (!selectedKeys) {
      // Actualizar el estado interno si no se usa selectedKeys.
      setInternalSelectedKeys(selectedOptions.map((option) => option.text));
    }
    setOptions([...tempOptions]);
    onApply(selectedOptions);
    setIsListVisible(false);
    if (dropdownRef.current) {
      dropdownRef.current.hidden = true;
    }
  };

  const handleClearAll = () => {
    const clearedOptions = options.map(option => ({
      ...option,
      checked: false,
    }));

    setOptions(clearedOptions);         // Limpia opciones base
    setTempOptions(clearedOptions);     // Limpia las opciones filtradas temporalmente
    setInternalSelectedKeys([]);        // Limpia estado interno
    setSearchTerm('');                  // Limpia el input de búsqueda
    setValueSelec('');                  // Limpia el label/tooltip
    setSelectAll(false);                // Desactiva el "Seleccionar todo"
    onApply([]);                        // Aplica el cambio hacia el componente padre
  };

  const MARGIN_FROM_VIEWPORT_EDGE = 8;

  const computeInitialPanelPosition = (): React.CSSProperties => {
    if (!triggerRef.current) {
      return {};
    }
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const estimatedPanelWidth = parseInt(String(dropdownStyle?.width ?? 300), 10) || 300;

    const fitsToTheRight = triggerRect.left + estimatedPanelWidth + MARGIN_FROM_VIEWPORT_EDGE <= window.innerWidth;

    const position: React.CSSProperties = {
      position: 'fixed',
      top: triggerRect.bottom + 4,
    };

    if (fitsToTheRight) {
      position.left = triggerRect.left;
    } else {
      // No entra hacia la derecha: se abre pegado al borde derecho del control.
      position.right = Math.max(MARGIN_FROM_VIEWPORT_EDGE, window.innerWidth - triggerRect.right);
    }

    return position;
  };

  const toggleListVisibility = () => {
    const willOpen = !isListVisible;
    if (willOpen) {
      setPanelPosition(computeInitialPanelPosition());
    }
    setIsListVisible(willOpen);
    if (dropdownRef.current) {
      dropdownRef.current.hidden = !willOpen;
    }
    setTempOptions([...options]);
  };

  // Una vez que el panel ya esta en el DOM y visible, se corrige la posicion con su tamano
  // real (el ancho/alto reales pueden diferir de la estimacion inicial segun el contenido),
  // evitando que se salga de la pantalla por abajo o por la derecha/izquierda.
  useLayoutEffect(() => {
    if (!isListVisible || !dropdownRef.current || !triggerRef.current) {
      return;
    }
    const panelRect = dropdownRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();

    setPanelPosition((current) => {
      const next: React.CSSProperties = { ...current };

      const fitsToTheRight = triggerRect.left + panelRect.width + MARGIN_FROM_VIEWPORT_EDGE <= window.innerWidth;
      if (fitsToTheRight) {
        next.left = triggerRect.left;
        next.right = undefined;
      } else {
        next.left = undefined;
        next.right = Math.max(MARGIN_FROM_VIEWPORT_EDGE, window.innerWidth - triggerRect.right);
      }

      const fitsBelow = triggerRect.bottom + panelRect.height + MARGIN_FROM_VIEWPORT_EDGE <= window.innerHeight;
      if (fitsBelow) {
        next.top = triggerRect.bottom + 4;
        next.bottom = undefined;
      } else {
        next.top = undefined;
        next.bottom = Math.max(MARGIN_FROM_VIEWPORT_EDGE, window.innerHeight - triggerRect.top + 4);
      }

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListVisible]);


  // verificar si todas las opciones tienen la propiedad "active" para mostrar el separador entre activos e inactivos
  const hasActiveProperty = filteredOptions.every(option => option.hasOwnProperty('active'));

  // separar opciones activas e inactivas
  const optionsActive = filteredOptions.filter(option => option?.active === "1");
  const optionsInactive = filteredOptions.filter(option => option?.active === "0");
  const isInactives = optionsInactive.length > 0;

  return (
    <div ref={triggerRef}>
      <TooltipHost id={'tooltip'} content={valueSelec} hidden={hiddenTooltip}>
        <Dropdown
          options={[]}
          styles={titleStyle}
          label={title}
          onClick={toggleListVisibility}
          placeholder={valueSelec}
          onRenderContainer={(props) => {
            return (
              <div
                ref={dropdownRef}
                style={{
                  border: '1px solid #ccc',
                  padding: '16px',
                  width: '300px',
                  position: 'absolute',
                  backgroundColor: 'white',
                  zIndex: 10,
                  ...dropdownStyle,
                  ...panelPosition,
                }}
              >
                <input type="text" placeholder={t("Buscar")} value={searchTerm} onChange={handleSearchChange} style={{ width: '90%', marginBottom: '8px', padding: '8px' }} />

                <div style={{ marginBottom: '8px' }}>
                  <input type="checkbox" checked={selectAll} onChange={handleSelectAllChange} />
                  <label style={{ marginLeft: '8px' }}>{t('Seleccionar todo')}</label>
                </div>

                <div
                  style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    marginBottom: '16px',
                  }}
                >
                  {!hasActiveProperty ? filteredOptions.map((option) => (
                    <div key={option.key} style={{ marginBottom: '8px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div>
                          <input type="checkbox" checked={option.checked} onChange={() => handleOptionChange(option.key)} />
                          <label style={{ marginLeft: '8px' }}>{option.text}</label>
                        </div>
                        {option.ariaLabel ? (
                          <span className="" style={{ fontSize: '10px', marginLeft: '28px' }}>
                            {option.ariaLabel}
                          </span>
                        ) : null}
                      </div>
                      {option.title ? <span style={{ fontSize: '10px', marginBottom: '8px', marginRight: '8px' }}>{option.title}</span> : null}
                    </div>
                  )) : (
                    <>
                      {optionsActive.map((option) => (
                        <div key={option.key} style={{ marginBottom: '8px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div>
                              <input type="checkbox" checked={option.checked} onChange={() => handleOptionChange(option.key)} />
                              <label style={{ marginLeft: '8px' }}>{option.text}</label>
                            </div>
                            {option.ariaLabel ? (
                              <span className="" style={{ fontSize: '10px', marginLeft: '28px' }}>
                                {option.ariaLabel}
                              </span>
                            ) : null}
                          </div>
                          {option.title ? <span style={{ fontSize: '10px', marginBottom: '8px', marginRight: '8px' }}>{option.title}</span> : null}
                        </div>
                      ))}

                      {/* Border separador */}
                      {isInactives && (
                          <div style={{ borderBottom: '2px solid #ccc', margin: '8px auto', width: '97%' }} />
                      )}

                      {optionsInactive.map((option) => (
                        <div key={option.key} style={{ marginBottom: '8px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div>
                              <input type="checkbox" checked={option.checked} onChange={() => handleOptionChange(option.key)} />
                              <label style={{ marginLeft: '8px' }}>{option.text}</label>
                            </div>
                            {option.ariaLabel ? (
                              <span className="" style={{ fontSize: '10px', marginLeft: '28px' }}>
                                {option.ariaLabel}
                              </span>
                            ) : null}
                          </div>
                          {option.title ? <span style={{ fontSize: '10px', marginBottom: '8px', marginRight: '8px' }}>{option.title}</span> : null}
                        </div>
                      ))}
                    </>
                  )}
                </div>
                <button
                  onClick={handleApply}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#007BFF',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {t('Aplicar')}
                </button>

                <button
                  onClick={handleClearAll}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    marginTop: '8px',
                  }}
                >
                  {t('Limpiar')}
                </button>
              </div>
            );
          }}
        />
      </TooltipHost>
    </div>
  );
};

export default CheckboxFilterComponent;
